// Clearing an integration's cache. The backend runs it as a job with no push
// channel, so the modal stays open and polls until it reaches a terminal status.

import { useCallback, useEffect, useRef, useState } from 'react';

import IntegrationsApi from '../../../api/integrations';

const POLL_INTERVAL_MS = 5000;
const TERMINAL_STATUSES = ['completed', 'failed'];

export default function useCacheJob({ integrationId, onFinished }) {
  const [visible, setVisible] = useState(false);
  const [running, setRunning] = useState(false);
  const intervalRef = useRef(null);

  const stopPolling = useCallback(() => {
    if (!intervalRef.current) return;

    clearInterval(intervalRef.current);
    intervalRef.current = null;
  }, []);

  // Leaving the screen mid-job must not leave the timer running behind it.
  useEffect(() => stopPolling, [stopPolling]);

  const poll = useCallback(() => {
    // Starting a second job would otherwise leave the first interval orphaned.
    stopPolling();

    intervalRef.current = setInterval(async () => {
      try {
        const response = await IntegrationsApi.fetchIntegrationByUuid(
          integrationId,
        );

        const status = response?.data?.attributes?.clean_cache_status;

        if (response && TERMINAL_STATUSES.includes(status)) {
          stopPolling();
          setRunning(false);
          setVisible(false);
          onFinished?.(status);
        }
      } catch (err) {
        stopPolling();
        setRunning(false);
      }
    }, POLL_INTERVAL_MS);
  }, [integrationId, onFinished, stopPolling]);

  const start = useCallback(async () => {
    try {
      setRunning(true);

      const response = await IntegrationsApi.clearCache(integrationId);

      if (response.status === 200) {
        poll();
      } else {
        setRunning(false);
      }
    } catch (err) {
      setRunning(false);
    }
  }, [integrationId, poll]);

  const toggle = useCallback(() => setVisible((previous) => !previous), []);

  return { visible, running, start, toggle };
}
